import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const PAGE_SIZE = 25;
const CACHE_KEY_PRODUCTS = 'inventory_products_cache';

// Same accumulating "load more" fetch pattern as useProductsPage — see that
// file for the reset/append reasoning. Inventory additionally keeps its own
// product-picker list (for Add stock) cached under the existing
// inventory_products_cache key, unchanged from the old page.
const useInventoryPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [sortModel, setSortModel] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState([]);

  const [products, setProducts] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [productPickerId, setProductPickerId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [previousQuantity, setPreviousQuantity] = useState(null);
  const [lowStockThreshold, setLowStockThreshold] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuItem, setRowMenuItem] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargets, setConfirmTargets] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!config?.backendUrl || !companyId) return;
    try {
      const cached = localStorage.getItem(CACHE_KEY_PRODUCTS);
      if (cached) setProducts(JSON.parse(cached));
    } catch (_e) {
      // ignore malformed cache
    }
    axios.get(`${config.backendUrl}/products?companyId=${companyId}`)
      .then((res) => {
        setProducts(res.data);
        try { localStorage.setItem(CACHE_KEY_PRODUCTS, JSON.stringify(res.data)); } catch (_e) { /* ignore */ }
      })
      .catch((error) => console.error('Error fetching products', error));
  }, [config, companyId]);

  const fetchInventory = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (search) params.search = search;
    if (sortModel[0]) {
      params.sortBy = sortModel[0].field;
      params.sortDir = sortModel[0].sort;
    }
    axios.get(`${config.backendUrl}/inventory`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching inventory', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /inventory · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, sortModel, t]);

  useEffect(() => { fetchInventory(true); }, [fetchInventory]);

  const loadMore = () => fetchInventory(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  // ---- Row actions menu ----------------------------------------------- //
  const openRowMenu = (event, item) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setRowMenuItem(item);
  };
  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuItem(null);
  };

  // ---- Add stock / edit drawer ----------------------------------------- //
  const openAddDrawer = () => {
    setDrawerMode('create');
    setProductPickerId('');
    setProductSearch('');
    setQuantity(0);
    setPreviousQuantity(null);
    setLowStockThreshold('');
    setEditingItem(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (item) => {
    setDrawerMode('edit');
    setProductPickerId(item.productId);
    setQuantity(item.quantity);
    setPreviousQuantity(item.quantity);
    setLowStockThreshold(item.lowStockThreshold != null ? String(item.lowStockThreshold) : '');
    setEditingItem(item);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  const filteredProducts = useMemo(() => {
    const existingProductIds = new Set(rows.map((r) => r.productId));
    const list = drawerMode === 'create' ? products.filter((p) => !existingProductIds.has(p._id)) : products;
    if (!productSearch) return list;
    const term = productSearch.toLowerCase();
    return list.filter((p) => p.productName?.toLowerCase().includes(term) || p.productDescription?.toLowerCase().includes(term));
  }, [products, productSearch, drawerMode, rows]);

  const pickedProduct = useMemo(() => products.find((p) => p._id === productPickerId), [products, productPickerId]);

  const handleSave = async () => {
    if (saving) return;
    if (!productPickerId) {
      notify(t('Please select a product'), 'warning');
      return;
    }
    setSaving(true);
    try {
      if (drawerMode === 'create') {
        await axios.post(`${config.backendUrl}/inventory`, {
          productId: productPickerId,
          quantity,
          lowStockThreshold: lowStockThreshold === '' ? null : Number(lowStockThreshold),
        });
        notify(t('Successfully Added!'));
      } else {
        await axios.put(`${config.backendUrl}/inventory/${editingItem.invertoryId}`, {
          quantity,
          lowStockThreshold: lowStockThreshold === '' ? null : Number(lowStockThreshold),
        });
        notify(t('Inventory item updated successfully!'));
      }
      setDrawerOpen(false);
      fetchInventory();
    } catch (error) {
      console.error(error);
      notify(drawerMode === 'create' ? t('Error creating inventory item') : t('Error updating inventory item'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete confirm ---------------------------------------------------//
  const openDeleteConfirm = (items) => {
    setConfirmTargets(items);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(confirmTargets.map((item) => axios.delete(`${config.backendUrl}/inventory/${item.invertoryId}`)));
      notify(t('Inventory item(s) successfully removed!'));
      setSelection([]);
      setConfirmOpen(false);
      fetchInventory();
    } catch (error) {
      console.error('Error removing inventory items:', error);
      notify(t('Error removing inventory items'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const selectedItems = useMemo(() => rows.filter((r) => selection.includes(r.invertoryId)), [rows, selection]);

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search);

  return {
    t, companyId, rows, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, sortModel, setSortModel,
    loadingMore, loadMore, remaining, nextBatchSize,
    selection, setSelection, selectedItems,
    rowMenuAnchor, rowMenuItem, openRowMenu, closeRowMenu,
    drawerOpen, drawerMode, closeDrawer, openAddDrawer, openEditDrawer, handleSave, saving,
    productPickerId, setProductPickerId, productSearch, setProductSearch, filteredProducts, pickedProduct,
    quantity, setQuantity, previousQuantity, lowStockThreshold, setLowStockThreshold, editingItem,
    confirmOpen, setConfirmOpen, confirmTargets, deleting, openDeleteConfirm, handleConfirmDelete,
    snackbar, setSnackbar, fetchInventory,
  };
};

export default useInventoryPage;
